// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';

import { spacing, fontSize, fontWeight } from 'presentations/views/styles';
import Dialog from 'presentations/views/components/atoms/Dialog';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import Select from 'presentations/views/components/atoms/Select';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import {
  black,
  gray01,
  gray04,
  primary,
  secondary,
} from 'presentations/views/styles/color';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  getPlanName,
  IntervalType,
  PlanId,
} from 'domains/models/subscription/planId';
import { Command, useDashboardSeatEditForm } from './hooks';
import TextField from '../../atoms/TextField';
import { formatToDate, getDateTime } from 'infras/datetime';
import { formatPrice } from 'presentations/views/helpers/formatter';
import Skeleton from '../../atoms/Skeleton';
import ConfirmationDialog from '../../molecules/ConfirmationDialog';

interface Props {
  viewer: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = useDashboardSeatEditForm({
    viewer: props.viewer,
  });

  const newQuantity = actions.calcNewQuantity(
    state.inputingCommand,
    state.inputingQuantity,
  );
  const isDisabled =
    typeof state.inputingQuantity !== 'number' ||
    state.inputingQuantity === 0 ||
    newQuantity < 0 ||
    newQuantity < state.teamUsers.length ||
    state.isPreviewLoading;

  const commandOptions: any = [
    { text: t('Add'), value: Command.Add },
    { text: t('Remove'), value: Command.Remove },
  ];

  return (
    <StyledDialog
      isOpen={state.isOpen}
      onClose={actions.onCancel}
      title={t('title')}
      isFillWidth
      actions={
        <DialogSubmitField
          onSubmit={actions.onConfirm}
          onCancel={actions.onCancel}
          isDisabled={isDisabled}
          submitLabel={t('Submit')}
        />
      }
    >
      <Block>
        <SelectContainer>
          <SeatSelectLabel>{t('Seat count')}</SeatSelectLabel>
          <SelectContainerRight>
            <TextFieldWrap
              name={'quantity'}
              type="number"
              value={state.inputingQuantity}
              width={80}
              onChange={actions.onInputChangeQuantity}
              placeholder={'3'}
            />
            <SelectWrap
              name={`command`}
              value={state.inputingCommand}
              onChange={actions.onInputChangeCommand}
              isFullWidth
              options={commandOptions}
            />
          </SelectContainerRight>
        </SelectContainer>
        {state.inputingQuantity > 0 && !state.error && (
          <PreviewContainer>
            {state.isPreviewLoading ? (
              <>
                <PreviewItem>
                  <Skeleton width="260px" height="16px" />
                </PreviewItem>
                <PreviewItem>
                  <Skeleton width="150px" height="16px" />
                  <Skeleton width="54px" height="16px" />
                </PreviewItem>
                <PreviewItem>
                  <Skeleton width="150px" height="16px" />
                  <Skeleton width="54px" height="16px" />
                </PreviewItem>
                <PreviewItem>
                  <Skeleton width="150px" height="16px" />
                  <Skeleton width="54px" height="16px" />
                </PreviewItem>
              </>
            ) : (
              <>
                <PreviewItem>{`${t('Amount')} ${
                  state.inputingCommand === Command.Add
                    ? t('Additions')
                    : t('Deletions')
                }（${getPlanName(
                  props.viewer.planId,
                  props.viewer.planVersion,
                )}/${t(
                  state.previewInvoiceItem?.intervalType ===
                    IntervalType.Annually
                    ? 'Annually'
                    : 'Monthly',
                )}）`}</PreviewItem>
                <PreviewItem>
                  <span>{t('Start date')}</span>
                  <span>
                    {formatToDate(
                      getDateTime(
                        state.previewInvoiceItem?.upcomingPeriodStart! * 1000,
                      ),
                      state.userProfile.timeZone,
                    )}
                  </span>
                </PreviewItem>
                {state.previewInvoiceItem &&
                  state.previewInvoiceItem.prorationAmount !== 0 && (
                    <PreviewItem>
                      <span>{t('Proration')}</span>
                      <span>
                        {state.previewInvoiceItem &&
                          state.previewInvoiceItem.prorationAmount !== 0 && (
                            <>
                              {formatPrice(
                                state.previewInvoiceItem.prorationAmount +
                                  state.previewInvoiceItem.prorationTaxAmount,
                                state.previewInvoiceItem.currency,
                              )}
                            </>
                          )}
                      </span>
                    </PreviewItem>
                  )}
                <PreviewItem>
                  <span>
                    {state.previewInvoiceItem?.intervalType ===
                    IntervalType.Annually
                      ? t('Annually amount')
                      : t('Monthly amount')}
                    （{t('Next cycle')}）
                  </span>
                  <span>
                    {state.previewInvoiceItem &&
                      formatPrice(
                        state.previewInvoiceItem.basicAmount +
                          state.previewInvoiceItem.basicTaxAmount,
                        state.previewInvoiceItem.currency,
                      )}
                  </span>
                </PreviewItem>
              </>
            )}
          </PreviewContainer>
        )}
      </Block>
      {state.inputingQuantity === 0 && (
        <Description isPreview={false}>{t('description')}</Description>
      )}
      {!state.error && state.previewInvoiceItem && (
        <Description isPreview={true}>{t('preview description')}</Description>
      )}
      {state.error && (
        <>
          <Error>{state.error}</Error>
          <PageLinkContainer>
            <LinkIcon name="chevron-right" />
            <PageLink onClick={actions.onClick}>
              {t('User management')}
            </PageLink>
          </PageLinkContainer>
        </>
      )}
      {state.isShowConfirm && (
        <ConfirmationDialog
          isLoading={state.isLoading}
          isOpen={true}
          onSubmit={actions.onSubmit}
          onCancel={actions.onCloseDialog}
          title={t('Update?')}
          text={t('Update? text', { newQuantity })}
        />
      )}
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: 400px;
  }
`;
const Block = styled.div`
  margin-bottom: ${spacing(24)};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const TextFieldWrap = styled(TextField)`
  margin-right: ${spacing(8)};
`;
const SelectWrap = styled(Select)`
  width: 120px;
  .MuiSelect-select {
    font-size: ${fontSize(14)};
  }
`;

const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const SeatSelectLabel = styled.span`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
`;
const SelectContainerRight = styled.div`
  display: flex;
  align-items: center;
`;
const Description = styled.div<{
  isPreview: boolean;
}>`
  font-size: ${fontSize(14)};
  margin: ${spacing(24)} 0;
  color: ${(props) => (props.isPreview ? gray01(props) : black(props))};
`;
const Error = styled.div`
  font-size: ${fontSize(14)};
  margin: ${spacing(24)} 0;
  color: ${secondary};
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${spacing(24)};
`;
const PreviewItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${fontSize(14)};
  border-bottom: 1px solid ${gray04};
  padding: ${spacing(12)} 0;
  :first-of-type {
    font-weight: ${fontWeight('bold')};
    border-top: 1px solid ${gray04};
  }
`;
const PageLinkContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(8)};
  margin-bottom: ${spacing(28)};
`;
const LinkIcon = styled(Icon)`
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
  margin-right: ${spacing(4)};
`;
const PageLink = styled(Link)`
  color: ${black};
`;
